import { NavigableTreeNode, NodeId } from '../types';

/**
 * Flatten the nodes into a single array, only including nodes that are currently visible.
 * @param nodes The nodes to flatten.
 * @returns The flattened nodes.
 */
function flattenNodes(nodes: NavigableTreeNode[]) {
  const flatNodes: NavigableTreeNode[] = [];

  function traverse(node: NavigableTreeNode) {
    flatNodes.push(node);
    if (node.hasChildren && node.isExpanded && node.children) {
      node.children.forEach(traverse);
    }
  }

  nodes.forEach(traverse);
  return flatNodes;
}

/**
 * Find the next node in the tree. Used for keyboard navigation (Right arrow key).
 * @param nodes The nodes to look through.
 * @param currentNodeId The node id to start from.
 * @returns The next node.
 */
export function findNextNode(
  nodes: NavigableTreeNode[],
  currentNodeId: NodeId,
): NavigableTreeNode | null {
  const flatNodes: NavigableTreeNode[] = flattenNodes(nodes);
  const currentIndex = flatNodes.findIndex(
    (node) => node.nodeId === currentNodeId,
  );
  return currentIndex >= 0 && currentIndex < flatNodes.length - 1
    ? flatNodes[currentIndex + 1]
    : null;
}

/**
 * Find the previous node in the tree. Used for keyboard navigation (Left arrow key).
 * @param nodes The nodes to look through.
 * @param currentNodeId The node id to start from.
 * @returns The previous node.
 */
export function findPreviousNode(
  nodes: NavigableTreeNode[],
  currentNodeId: NodeId,
): NavigableTreeNode | null {
  const flatNodes: NavigableTreeNode[] = flattenNodes(nodes);

  const currentIndex = flatNodes.findIndex(
    (node) => node.nodeId === currentNodeId,
  );
  return currentIndex > 0 ? flatNodes[currentIndex - 1] : null;
}

/**
 * Find the first node in the tree. Used for keyboard navigation (for example, Home key).
 * @param nodes The nodes to look through.
 * @returns The first node.
 */
export function findFirstNode(
  nodes: NavigableTreeNode[],
): NavigableTreeNode | null {
  return nodes.length > 0 ? nodes[0] : null;
}

/**
 * Find the last node in the tree. Used for keyboard navigation (for example, End key).
 * @param nodes The nodes to look through.
 * @returns The last node.
 */
export function findLastNode(
  nodes: NavigableTreeNode[],
): NavigableTreeNode | null {
  const flatNodes: NavigableTreeNode[] = flattenNodes(nodes);
  return flatNodes.length > 0 ? flatNodes[flatNodes.length - 1] : null;
}

/**
 * Find the parent node of a given node. Used for keyboard navigation (Left arrow key).
 * @param nodes The nodes to look through.
 * @param targetNodeId The node id to find the parent of.
 * @returns The parent node.
 */
export function findParentNode(
  nodes: NavigableTreeNode[],
  targetNodeId: NodeId,
): NavigableTreeNode | null {
  for (const node of nodes) {
    if (node.children) {
      if (node.children.some((child) => child.nodeId === targetNodeId)) {
        return node;
      }
      const found = findParentNode(node.children, targetNodeId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}
